import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase } from 'lucide-react';
import { CURRENCIES } from '@/lib/calculationEngine';
import { LabeledSlider } from './fields/LabeledSlider';

export function ImplementationApproach({ config, updateConfig }) {
  const { t } = useTranslation();

  const approaches = [
    { value: 'internal', label: t('approach.internal'), desc: t('approach.internalDesc') },
    { value: 'consultant', label: t('approach.consultant'), desc: t('approach.consultantDesc') },
    { value: 'mixed', label: t('approach.mixed'), desc: t('approach.mixedDesc') },
  ];
  const mixRatio = config.mixRatio ?? 50;
  return (
    <Card className="bg-white border-[#DDE1E5]" data-testid="implementation-approach-section">
      <CardHeader className="pb-4">
        <CardTitle
          className="flex items-center gap-2 text-lg text-[#171A1D]"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <Briefcase className="w-5 h-5 text-[#66707A]" />
          {t('approach.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-[#66707A] text-sm" id="approach-label">{t('approach.label')}</Label>
          <RadioGroup
            value={config.approach}
            onValueChange={v => updateConfig({ approach: v })}
            className="mt-2 space-y-2"
            aria-labelledby="approach-label"
          >
            {approaches.map(a => (
              <div
                key={a.value}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  config.approach === a.value
                    ? 'border-[#527F19]/50 bg-[#527F19]/5'
                    : 'border-[#DDE1E5] hover:border-[#DDE1E5]'
                }`}
              >
                <RadioGroupItem value={a.value} id={`approach-${a.value}`} data-testid={`approach-${a.value}`} />
                <Label htmlFor={`approach-${a.value}`} className="cursor-pointer flex-1">
                  <div className="text-sm font-medium text-[#171A1D]">{a.label}</div>
                  <div className="text-xs text-[#66707A]">{a.desc}</div>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {config.approach === 'mixed' && (
          <LabeledSlider
            label={t('approach.splitLabel')}
            value={mixRatio}
            onValueChange={v => updateConfig({ mixRatio: v })}
            min={10}
            max={90}
            step={5}
            display={(
              <span
                className="text-sm text-[#527F19]"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                data-testid="mix-ratio-value"
              >
                {mixRatio}% / {100 - mixRatio}%
              </span>
            )}
            ariaLabel={`${t('approach.moreInternal')} / ${t('approach.moreExternal')}: ${mixRatio}% / ${100 - mixRatio}%`}
            testId="mix-ratio-slider"
            captions={[t('approach.moreExternal'), t('approach.moreInternal')]}
          />
        )}

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-[#66707A] text-sm">{t('approach.currency')}</Label>
            <Select value={config.currency} onValueChange={v => updateConfig({ currency: v })}>
              <SelectTrigger aria-label={t('approach.currency')} className="mt-1.5 bg-white border-[#DDE1E5] text-[#171A1D]" data-testid="currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#DDE1E5]">
                {CURRENCIES.map(c => (
                  <SelectItem key={c.value} value={c.value} className="text-[#171A1D] focus:bg-[#F5F6F7] focus:text-[#171A1D]">
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#66707A] text-sm">{t('approach.internalRate')}</Label>
            <Input
              type="number"
              aria-label={t('approach.internalRate')}
              value={config.internalRate}
              onChange={e => updateConfig({ internalRate: Number(e.target.value) || 0 })}
              className="mt-1.5 bg-white border-[#DDE1E5] text-[#171A1D]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="internal-rate-input"
            />
          </div>
          <div>
            <Label className="text-[#66707A] text-sm">{t('approach.consultantRate')}</Label>
            <Input
              type="number"
              aria-label={t('approach.consultantRate')}
              value={config.consultantRate}
              onChange={e => updateConfig({ consultantRate: Number(e.target.value) || 0 })}
              className="mt-1.5 bg-white border-[#DDE1E5] text-[#171A1D]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="consultant-rate-input"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
